import { useState } from 'react';

import { ArrowDownLeftIcon, ArrowUpRightIcon, CloseIcon } from '@braskai/icons';
import { Button, Modal, Typography } from '@braskai/ui-kit';

import { useCurrentUser } from '@/entities/user/currentUser';

import { useCustomerPortal } from '@/shared/hooks/useCustomerPortal';
import { showErrorAlert } from '@/shared/lib/showErrorAlert';
import { PaymentInterval, Price } from '@/shared/types/Billing';

import { useUpgradeConfirmationModalStore } from '../../model/useUpgradeConfirmationModalStore';
import { SButtonContainer, SCloseButton, SContent, SHeader, SModalContainer, SPlanBlock, SPlanLabel } from './styled';

const formatDate = (date: Date | string) => {
  const dateObj = new Date(date);
  return dateObj.toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

const getDowngradeDate = (targetPrice: Price) => {
  const currentDate = new Date();
  const isYearlyPlan = targetPrice.recurring?.interval === PaymentInterval.Year;

  if (isYearlyPlan) {
    currentDate.setFullYear(currentDate.getFullYear() + 1);
  } else {
    currentDate.setMonth(currentDate.getMonth() + 1);
  }

  return formatDate(currentDate);
};

export const UpgradeConfirmationModal = () => {
  const [isLoading, setIsLoading] = useState(false);
  const isOpen = useUpgradeConfirmationModalStore((state) => state.isOpen);
  const close = useUpgradeConfirmationModalStore((state) => state.close);
  const currentPlan = useUpgradeConfirmationModalStore((state) => state.currentPlan);
  const targetPlan = useUpgradeConfirmationModalStore((state) => state.targetPlan);
  const targetPrice = useUpgradeConfirmationModalStore((state) => state.targetPrice);
  const minutesToAdd = useUpgradeConfirmationModalStore((state) => state.minutesToAdd);

  const { data: user } = useCurrentUser();
  const { openCustomerPortal } = useCustomerPortal();

  const handleUpgrade = async () => {
    if (!user?.subscription?.id || !targetPrice?.id) {
      return;
    }

    setIsLoading(true);
    try {
      await openCustomerPortal({
        navigateTo: 'updatePlan',
        priceId: targetPrice.id,
        subscriptionId: user.subscription.id,
      });
      close();
    } catch (error) {
      showErrorAlert(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    close();
  };

  if (!currentPlan || !targetPlan || !targetPrice) {
    return null;
  }

  return (
    <Modal open={isOpen} onClose={handleCancel}>
      <SModalContainer>
        {/* Header */}
        <SHeader>
          <Typography variant="title-4-md">You're about to upgrade!</Typography>
          <SCloseButton onClick={handleCancel}>
            <CloseIcon />
          </SCloseButton>
        </SHeader>

        {/* Content */}
        <SContent>
          {/* Current Plan Block */}
          <SPlanBlock>
            <SPlanLabel>
              <ArrowDownLeftIcon height={16} width={16} />
              <Typography color="grey.500" variant="body-2-md">
                Current plan
              </Typography>
            </SPlanLabel>
            <Typography color="grey.700" variant="body-2">
              You're currently on {currentPlan.name}, active until{' '}
              {user?.subscription?.current_period_end && formatDate(user.subscription.current_period_end)}.
            </Typography>
          </SPlanBlock>

          {/* New Plan Block */}
          <SPlanBlock $isNewPlan>
            <SPlanLabel $isNewPlan>
              <ArrowUpRightIcon height={16} width={16} />
              <Typography color="primary.500" variant="body-2-md">
                New plan
              </Typography>
            </SPlanLabel>
            <Typography color="grey.700" variant="body-2">
              You're switching to {targetPlan.name}. You'll immediately get new features plus {minutesToAdd || 0} extra
              minutes (the difference between plans).
            </Typography>
          </SPlanBlock>

          {/* Downgrade info */}
          <Typography color="grey.500" variant="body-2">
            You can switch back to your previous plan starting {getDowngradeDate(targetPrice)}.
          </Typography>
        </SContent>

        <SButtonContainer>
          <Button isLoading={isLoading} fullWidth onClick={handleUpgrade}>
            Upgrade now
          </Button>
        </SButtonContainer>
      </SModalContainer>
    </Modal>
  );
};
